import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { Observable, BehaviorSubject } from 'rxjs';
import { finalize, map, switchMap, take } from 'rxjs/operators';
import { OrganizationInterface, OrganizationInterfaceWithId } from '@sk/sk-interfaces';
import { LocalStorageService } from 'services/local-storage.service';
import { fromPromise } from 'rxjs/internal-compatibility';
import * as moment from 'moment';
import { AngularFireAuth } from '@angular/fire/auth';
import { error } from 'protractor';


@Injectable({
  providedIn: 'root'
})
export class OrganizationService {

  currentOrganization: OrganizationInterfaceWithId = null;
  private dataSource = new BehaviorSubject<OrganizationInterfaceWithId>(this.currentOrganization);
  organizationChanged = this.dataSource.asObservable();

  constructor(private firestoreService: AngularFirestore,
              private afAuth: AngularFireAuth,
              private storageService: LocalStorageService) {
  }

  /**
   * Returns the document for an organization
   */
  getOrganization(): Observable<OrganizationInterfaceWithId> {
    const organizationId = this.storageService.getOrganizationId();
    return this.firestoreService.collection('organizations').doc(organizationId).snapshotChanges()
      .pipe(
        map(a => {
          const data = a.payload.data() as OrganizationInterface;
          const id = a.payload.id;
          const exists = a.payload.exists;
          this.currentOrganization = { id, exists, ...data } as OrganizationInterfaceWithId;
          this.dataSource.next(this.currentOrganization);
          return { id, exists, ...data } as OrganizationInterfaceWithId;
        })
      );
  }


  getAuthorizedOrganization(): Observable<OrganizationInterfaceWithId> {
    return this.afAuth.idTokenResult.pipe()
      .pipe(
        switchMap(token => {
          if (token.claims.organizationId) {
            return this.firestoreService.collection('organizations').doc(token.claims.organizationId).snapshotChanges();
          }
          throw new Error('No authorized organization found');
        }),
        map(a => {
          const data = a.payload.data() as OrganizationInterface;
          const id = a.payload.id;
          const exists = a.payload.exists;
          this.currentOrganization = { id, exists, ...data } as OrganizationInterfaceWithId;
          this.dataSource.next(this.currentOrganization);
          return {...data, id, exists  } as OrganizationInterfaceWithId;
        })
      );
  }

  getCurrentOrganization() {
    if (this.currentOrganization !== null) {
      return this.currentOrganization;
    } else {
      this.getOrganization().pipe(take(1))
        .subscribe(() => this.getOrganization(), error => {
          console.error(new Error('From organization service. No Organization set'));
          return null;
        });
    }
  }

  updateOrganization(organization: OrganizationInterfaceWithId) {
    const organizationId = this.storageService.getOrganizationId();
    const org = {...organization}
    delete org.id;
    delete org.exists;
    this.currentOrganization = organization;
    return fromPromise(this.firestoreService
      .collection('organizations').doc(organizationId).update(org));
  }


  updateOrganizationInvoiceNumberById(nextInvoiceNumber: number, organizationId: string) {
    const updateObject = {
      nextInvoiceNumber
    };
    return fromPromise(this.firestoreService
      .collection('organizations').doc(organizationId).update(updateObject));
  }


  updateOrganizationFromNgrx(organization: OrganizationInterfaceWithId) {
    const organizationId = this.storageService.getOrganizationId();
    const orgObject = {
      ...organization
    };
    delete orgObject.id;
    delete orgObject.exists;
    return fromPromise(this.firestoreService
      .collection('organizations').doc(organizationId).update(orgObject));
  }


  getFireStoreOrganization(organizationId: string): Observable<OrganizationInterfaceWithId> {
    console.log(`RUN ORGANIZATION QUERY - ${moment().format('HH:mm:ss')}`);
    return this.firestoreService.collection('organizations').doc(organizationId).snapshotChanges()
      .pipe(
        map(a => {
          const data = a.payload.data() as OrganizationInterface;
          const id = a.payload.id;
          const exists = a.payload.exists;
          return { id, exists, ...data } as OrganizationInterfaceWithId;
        }),
        finalize(() => console.log('ORGANIZATION QUERY DONE'))
      );
  }


}

